import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type SideDrawerContext<T = unknown> = {
  open: boolean;
  contentKey: T | null;
  setContentKey: (key: T | null) => void;
  toggleContentKey: (key: T) => void;
  onClose: () => void;
};

interface SideDrawerUtils<T = unknown> {
  Provider: React.FC<{ children: ReactNode }>;
  useContext: () => SideDrawerContext<T>;
}

export const createSideDrawerContext = <T = unknown,>(): SideDrawerUtils<T> => {
  const Context = createContext<SideDrawerContext<T>>({
    open: false,
    contentKey: null,
    setContentKey: () => null,
    toggleContentKey: () => null,
    onClose: () => null,
  });

  const SideDrawerProvider = ({ children }: { children: ReactNode }) => {
    const [contentKey, setContentKey] = useState<T | null>(null);
    const [activeKey, setActiveKey] = useState<T | null>(null);

    const toggleContentKey = useCallback((key: T) => {
      setContentKey((currentKey) => (currentKey === key ? null : key));
    }, []);

    // In order to prevent `Content` from unmounting and disappearing suddenly
    // while the side drawer animates closed, we instead only 'swap out' the component
    // as required - typically just before opening the side drawer.
    // This means we can avoid any cumbersome animation logic / involving timers.
    useEffect(() => {
      if (contentKey) {
        setActiveKey(contentKey);
      } else {
        setActiveKey(null);
      }
    }, [contentKey]);

    const onClose = useCallback(() => setContentKey(null), []);

    const state: SideDrawerContext<T> = useMemo(
      () => ({
        open: !!contentKey,
        contentKey: activeKey,
        setContentKey,
        toggleContentKey,
        onClose,
      }),
      [contentKey, activeKey, toggleContentKey, onClose],
    );

    return <Context.Provider value={state}>{children}</Context.Provider>;
  };

  return {
    Provider: SideDrawerProvider,
    useContext: () => useContext(Context),
  };
};
